import React, { useRef, useState } from "react";

const MAX_BYTES = 5 * 1024 * 1024;

export default function BulkQuestionUpload({ ModelSelect, onImported }) {
  const [model, setModel] = useState("");
  const [json, setJson] = useState("");
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const inFlight = useRef(false);

  const readFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setResult(null);
    setError("");
    setJson("");
    if (file.size > MAX_BYTES) {
      setError("Choose a JSON file smaller than 5 MB.");
      return;
    }
    setReading(true);
    try {
      setJson((await file.text()).replace(/^\uFEFF/, ""));
    } catch {
      setError("Could not read this file. Try selecting it again.");
    } finally {
      setReading(false);
      event.target.value = "";
    }
  };

  const downloadExample = () => {
    const example = [{ id: "900001", q_title: "Example question", title: "Practice question", question: "Replace with your question text.", answer: "Replace with the answer.", subcategory_id: 1 }];
    const url = URL.createObjectURL(new Blob([JSON.stringify(example, null, 2)], { type: "application/json" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "practice-questions-example.json";
    link.click();
    URL.revokeObjectURL(url);
  };

  const upload = async () => {
    if (inFlight.current) return;
    setError("");
    setResult(null);
    let questions;
    try {
      if (!model) throw new Error("Select a question type first.");
      if (new Blob([json]).size > MAX_BYTES) throw new Error("JSON must be smaller than 5 MB.");
      try { questions = JSON.parse(json); } catch { throw new Error("Invalid JSON. Provide an array of question objects using double quotes and no trailing commas."); }
      if (!Array.isArray(questions) || !questions.length || questions.length > 500) {
        throw new Error("Provide an array of 1 to 500 questions.");
      }
    } catch (validationError) {
      setError(validationError.message);
      return;
    }
    inFlight.current = true;
    setBusy(true);
    try {
      const response = await fetch("/api/admin/questions/bulk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model, questions }),
      });
      const data = await response.json();
      setResult(data);
      if (!response.ok) setError(data.error || "Import failed.");
      else if (data.inserted > 0) onImported();
    } catch {
      setError("Could not confirm the import result. Refresh the question table before retrying; some questions may have been saved.");
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <section className="mb-6 rounded-lg border border-sky-200 bg-sky-50 p-4">
      <h3 className="text-lg font-bold text-slate-950">Bulk upload questions (JSON)</h3>
      <p className="mt-1 text-sm text-slate-600">Add up to 500 questions of one type at a time (5 MB maximum). Existing IDs and repeated IDs in the file are skipped. All rows must pass validation before saving.</p>
      <fieldset disabled={busy || reading} className="mt-4 space-y-4 disabled:opacity-60">
        <label className="block text-sm font-semibold text-slate-700">
          Question type
          <ModelSelect value={model} onChange={(event) => { setModel(event.target.value); setResult(null); setError(""); }} />
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm font-semibold text-slate-700">JSON file
            <input type="file" accept=".json,application/json" onChange={readFile} className="ml-3 max-w-full text-sm" />
          </label>
          <button type="button" onClick={downloadExample} className="text-sm font-semibold text-sky-700 underline">Download example JSON</button>
        </div>
        <label className="block text-sm font-semibold text-slate-700">
          Or paste / edit JSON
          <textarea value={json} onChange={(event) => { setJson(event.target.value); setResult(null); setError(""); }} spellCheck={false} placeholder={'[ { "id": "900001", "question": "Your question..." } ]'} className="mt-1 min-h-48 w-full rounded-lg border border-slate-200 bg-white p-3 font-mono text-sm" />
        </label>
        <p className="text-sm text-slate-600">Use an array of question objects. Set unique IDs and the correct subcategory_id for your questions. Include required fields for the selected type, plus media_link, image_link or option when needed. Option formats depend on the question type.</p>
        <button type="button" disabled={!model || !json.trim()} onClick={upload} className="rounded-lg bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50">{busy ? "Importing questions…" : reading ? "Reading file…" : "Import questions"}</button>
      </fieldset>
      <div aria-live="polite" className="mt-3 text-sm">
        {error && <p role="alert" className="font-semibold text-rose-700">{error}</p>}
        {result?.total !== undefined && <p className="font-semibold text-slate-800">Added: {result.inserted} · Skipped existing / repeated IDs: {result.skipped} · Failed: {result.failed}</p>}
        {result?.errors?.length > 0 && <ul className="mt-2 max-h-48 list-inside list-disc overflow-auto text-rose-700">{result.errors.map((item) => <li key={item.row}>Row {item.row}: {item.message}</li>)}</ul>}
      </div>
    </section>
  );
}
